import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import EducationHero from './components/EducationHero';
import BlogSection from './components/BlogSection';
import EducationInquiryForm from './components/EducationInquiryForm';
import { setPageMeta, injectJsonLd, removeJsonLd } from '../../utils/seo';

type ActiveSection = 'journal' | 'masterclass' | 'training';

const EducationPage = () => {
  const [activeSection, setActiveSection] = useState<ActiveSection>('journal');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    
    // Set page meta tags
    setPageMeta(
      'Educational Programs | Vionne Stability - Advanced Acupuncture Training & Certification',
      'Advanced acupuncture training programs, masterclasses, and professional certification courses. Learn the Vionne Stability Method and elevate your practice with cutting-edge techniques.',
      `${siteUrl}/education`
    );

    // Inject JSON-LD schema
    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'EducationalOrganization',
        '@id': `${siteUrl}/education#organization`,
        name: 'Vionne Stability Education',
        url: `${siteUrl}/education`,
        description: 'Advanced acupuncture training and certification programs for practitioners seeking to master the Vionne Stability Method',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Vancouver',
          addressRegion: 'BC',
          addressCountry: 'CA'
        },
        areaServed: {
          '@type': 'Country',
          name: 'Canada'
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Educational Programs',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Course',
                name: 'Stability Method Masterclass',
                description: 'Intensive training in facial structure regulation and nervous system optimization',
                provider: {
                  '@type': 'Organization',
                  name: 'Vionne Stability'
                },
                educationalLevel: 'Advanced',
                courseMode: 'In-person',
                hasCourseInstance: {
                  '@type': 'CourseInstance',
                  courseMode: 'In-person',
                  location: {
                    '@type': 'Place',
                    address: {
                      '@type': 'PostalAddress',
                      addressLocality: 'Vancouver',
                      addressRegion: 'BC',
                      addressCountry: 'CA'
                    }
                  }
                }
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Course',
                name: 'Advanced Acupuncture Certification',
                description: 'Professional certification program for experienced practitioners',
                provider: {
                  '@type': 'Organization',
                  name: 'Vionne Stability'
                },
                educationalLevel: 'Professional',
                courseMode: 'Blended'
              }
            }
          ]
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': `${siteUrl}/education#blog`,
        name: 'Vionne Stability Journal',
        description: 'Educational articles on nervous system regulation, facial structure, and holistic wellness',
        url: `${siteUrl}/education`,
        publisher: {
          '@type': 'Organization',
          name: 'Vionne Stability',
          url: siteUrl
        },
        blogPost: [
          {
            '@type': 'BlogPosting',
            '@id': `${siteUrl}/education#article-1`,
            headline: 'When the Face Holds What the Body Cannot Release',
            description: 'Understanding how chronic stress manifests in facial structure and the role of nervous system regulation',
            url: `${siteUrl}/education#article-1`,
            author: {
              '@type': 'Person',
              name: 'Vionne',
              url: `${siteUrl}/about`
            },
            publisher: {
              '@type': 'Organization',
              name: 'Vionne Stability'
            },
            datePublished: '2024-01-15',
            dateModified: '2024-01-15',
            articleSection: 'Nervous System & Facial Health',
            keywords: 'stress, facial structure, nervous system regulation, acupuncture'
          },
          {
            '@type': 'BlogPosting',
            '@id': `${siteUrl}/education#article-2`,
            headline: 'The Stability Framework: A Systems Approach',
            description: 'Exploring the comprehensive methodology behind facial structure regulation',
            url: `${siteUrl}/education#article-2`,
            author: {
              '@type': 'Person',
              name: 'Vionne',
              url: `${siteUrl}/about`
            },
            publisher: {
              '@type': 'Organization',
              name: 'Vionne Stability'
            },
            datePublished: '2024-02-10',
            dateModified: '2024-02-10',
            articleSection: 'Methodology',
            keywords: 'stability method, facial acupuncture, holistic approach'
          },
          {
            '@type': 'BlogPosting',
            '@id': `${siteUrl}/education#article-3`,
            headline: 'Beyond Aesthetics: Nervous System Coherence',
            description: 'How internal regulation creates lasting external change',
            url: `${siteUrl}/education#article-3`,
            author: {
              '@type': 'Person',
              name: 'Vionne',
              url: `${siteUrl}/about`
            },
            publisher: {
              '@type': 'Organization',
              name: 'Vionne Stability'
            },
            datePublished: '2024-03-05',
            dateModified: '2024-03-05',
            articleSection: 'Wellness Philosophy',
            keywords: 'nervous system, aesthetics, holistic wellness, internal regulation'
          }
        ]
      }
    ];

    injectJsonLd(schema);

    return () => {
      removeJsonLd();
    };
  }, []);

  return (
    <div className="min-h-screen bg-warmWhite">
      <Navbar />
      <main>
        <EducationHero setActiveSection={setActiveSection} />
        <BlogSection activeSection={activeSection} setActiveSection={setActiveSection} />
        <EducationInquiryForm />
      </main>
      <Footer />
    </div>
  );
};

export default EducationPage;